import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { FileText, Target, BookOpen, PenTool } from 'lucide-react';
import { NewPageFormState } from "../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { detectPageType, pageTypeOptions, PageType } from "../../new-page-smart-detection";
import { Separator } from "@/components/ui/separator";
import WritingStyleDropdown from "@/components/content-generator/editor/WritingStyleDropdown";

interface PageBasicsStepProps {
  value: NewPageFormState;
  onChange: (value: NewPageFormState) => void;
  showValidation?: boolean;
}

export const PageBasicsStep: React.FC<PageBasicsStepProps> = ({
  value,
  onChange,
  showValidation = false,
}) => {
  // Character counts
  const titleCharacterCount = value.title.length;
  const purposeCharacterCount = value.initial_request.length;
  const [detectedType, setDetectedType] = useState<PageType>();
  const availablePageTypes = pageTypeOptions.map(p => p.value);

  // Smart detection effect - runs when component mounts with existing purpose text
  useEffect(() => {
    if (value.initial_request.length >= 20) {
      const detected = detectPageType(value.initial_request, availablePageTypes);
      
      if(detected) {
        setDetectedType(detected);
        if (!value.type) {
          onChange({ ...value, type: detected });
        }
      } else {
        // If no type detected and user hasn't manually selected one, suggest "Other"
        if (!value.type && value.initial_request.length >= 20) {
          setDetectedType('Other');
          onChange({ ...value, type: 'Other' });
        }
      }
    }
  }, [value.initial_request, onChange, availablePageTypes, value.type]);

  return (
    <div className="space-y-6">
      {/* Title Field */}
      <div className="space-y-2">
        <Label htmlFor="title" className="text-sm font-medium flex items-center gap-2">
          <FileText size={16} className="text-purple-600 dark:text-purple-400" />
          Choose a page title
        </Label>
        <Input
          id="title"
          placeholder="e.g., Q3 Marketing Strategy, Product Launch Plan, User Research Summary..."
          value={value.title}
          maxLength={30}
          autoFocus
          onChange={e => onChange({ ...value, title: e.target.value })}
          className={cn(
            "transition-all duration-200",
            showValidation && titleCharacterCount < 3 ? "border-red-500" : 
            titleCharacterCount >= 3 ? "border-green-500 dark:border-green-400" : ""
          )}
        />
        
        {/* Title feedback */}
        <div className="flex justify-between items-center text-xs">
          <div className={cn(
            "transition-colors duration-200",
            titleCharacterCount < 3 ? "text-amber-600 dark:text-amber-400" :
            "text-green-600 dark:text-green-400"
          )}>
            {titleCharacterCount < 3 ? `${3 - titleCharacterCount} more characters needed` :
             "Perfect!"}
          </div>
          <div className="text-muted-foreground font-mono">
            {titleCharacterCount} / 30
          </div>
        </div>
      </div>

      {/* Purpose Field */}
      <div className="space-y-2">
        <Label htmlFor="initial_request" className="text-sm font-medium flex items-center gap-2">
          <Target size={16} className="text-purple-600 dark:text-purple-400" />
          What is this page for?
        </Label>
        <Textarea
          id="initial_request"
          rows={4}
          maxLength={1000}
          placeholder="Describe the purpose of this page in detail... (e.g., 'A comprehensive blog post about the future of AI in content creation, covering current trends, challenges, and opportunities for marketers')"
          value={value.initial_request}
          onChange={e => onChange({ ...value, initial_request: e.target.value })}
          className={cn(
            "resize-none transition-all duration-200",
            showValidation && purposeCharacterCount < 20 ? "border-red-500" :
            purposeCharacterCount >= 20 ? "border-green-500 dark:border-green-400" : ""
          )}
        />
        
        {/* Purpose feedback */}
        <div className="flex justify-between items-center text-xs">
          <div className={cn(
            "transition-colors duration-200",
            purposeCharacterCount < 20 ? "text-amber-600 dark:text-amber-400" :
            "text-green-600 dark:text-green-400"
          )}>
            {purposeCharacterCount < 20 ? `${20 - purposeCharacterCount} more characters needed` :
             "Perfect length!"}
          </div>
          <div className="text-muted-foreground font-mono">
            {purposeCharacterCount} / 1000
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      {/* Page Type Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <BookOpen size={16} className="text-purple-600 dark:text-purple-400" />
          What type of page is this?
        </Label>
        <Select
          value={value.type}
          onValueChange={t => {
            onChange({ ...value, type: t as PageType });
          }}
        >
          <SelectTrigger
            className={cn(
              "w-full transition-all duration-200",
              value.type ? "border-green-500 dark:border-green-400" : ""
            )}
            aria-label="Page type selection"
          >
            <SelectValue placeholder="Choose the best type for your page..." />
          </SelectTrigger>
          <SelectContent>
            {pageTypeOptions.map(opt =>
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <Separator className="my-4" />

      {/* Writing Style Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <PenTool size={16} className="text-purple-600 dark:text-purple-400" />
          Writing Style & Tone
        </Label>
        
        <WritingStyleDropdown
          selectedAssistantId={value.writing_style}
          onAssistantChange={(assistantId) => onChange({ ...value, writing_style: assistantId })}
        />
      </div>
    </div>
  );
}; 